import {
  IKpiDemographicBucket,
  IKpiDemographics,
  IKpiSnapshot,
} from '@/types/response';
import { Card, Empty, Segmented, Table, Typography } from 'antd';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { SERIES_COLORS, formatNumber } from './KpiChart';

const { Text } = Typography;

type Dimension = keyof IKpiDemographics;

const DIMENSION_OPTIONS: { label: string; value: Dimension }[] = [
  { label: '性別', value: 'gender' },
  { label: '年齢層', value: 'ageBand' },
  { label: '都道府県', value: 'prefecture' },
];

const UNKNOWN = 'unknown';

const GENDER_LABELS: Record<string, string> = {
  female: '女性',
  male: '男性',
  other: 'その他',
};

const AGE_ORDER = [
  'under20',
  '20s',
  '30s',
  '40s',
  '50s',
  '60s',
  '70s',
  '80plus',
];
const ageLabel = (key: string) =>
  key === 'under20'
    ? '20歳未満'
    : key === '80plus'
      ? '80歳以上'
      : `${key.replace('s', '')}代`;

/** 都道府県は上位だけ出し、残りは「その他」にまとめる（少人数の県で個人が推測されないように） */
const PREFECTURE_TOP = 10;

type Row = IKpiDemographicBucket & { label: string };

const sum = (rows: IKpiDemographicBucket[], k: 'registered' | 'active') =>
  rows.reduce((acc, r) => acc + r[k], 0);

/** 表示順に並べ、未登録は最後に置く */
const toRows = (
  dimension: Dimension,
  buckets: IKpiDemographicBucket[],
): Row[] => {
  const known = buckets.filter((b) => b.key !== UNKNOWN);
  const unknown = buckets.filter((b) => b.key === UNKNOWN);
  let rows: Row[];

  if (dimension === 'gender') {
    rows = ['female', 'male', 'other']
      .map(
        (key) =>
          known.find((b) => b.key === key) ?? { key, registered: 0, active: 0 },
      )
      .map((b) => ({ ...b, label: GENDER_LABELS[b.key] ?? b.key }));
  } else if (dimension === 'ageBand') {
    rows = AGE_ORDER.map(
      (key) =>
        known.find((b) => b.key === key) ?? { key, registered: 0, active: 0 },
    ).map((b) => ({ ...b, label: ageLabel(b.key) }));
  } else {
    const sorted = [...known].sort((a, b) => b.registered - a.registered);
    const rest = sorted.slice(PREFECTURE_TOP);
    rows = sorted.slice(0, PREFECTURE_TOP).map((b) => ({ ...b, label: b.key }));
    if (rest.length > 0) {
      rows.push({
        key: '__rest',
        label: `その他（${rest.length}都道府県）`,
        registered: sum(rest, 'registered'),
        active: sum(rest, 'active'),
      });
    }
  }

  return [...rows, ...unknown.map((b) => ({ ...b, label: '未登録' }))];
};

const pct = (v: number, total: number) =>
  total ? Math.round((v / total) * 1000) / 10 : null;

/** 構成比の横棒。登録とアクティブを上下に並べて比べる */
const ShareBar: React.FC<{ value: number | null; color: string }> = ({
  value,
  color,
}) => (
  <div className='h-[6px] w-full rounded-[3px] bg-[rgba(0,0,0,0.06)]'>
    <div
      className='h-full rounded-[3px]'
      style={{ width: `${Math.min(value ?? 0, 100)}%`, background: color }}
    />
  </div>
);

interface IPanelProps {
  demographics: Partial<IKpiDemographics> | undefined;
  /** 集計した時刻（ISO 文字列またはミリ秒） */
  asOf: string | number;
  /** 切り替えに出す区分 */
  dimensions?: Dimension[];
  /** 表の上の説明。区分ごとの登録・アクティブの合計を受け取る */
  note: (total: { registered: number; active: number }) => React.ReactNode;
  /** まだ集計が無いときの説明 */
  emptyText?: string;
}

/** 属性ごとの登録・アクティブの構成比（全体ダッシュボードと企業別ダッシュボードで共通） */
export const DemographicsPanel: React.FC<IPanelProps> = ({
  demographics,
  asOf,
  dimensions = DIMENSION_OPTIONS.map((o) => o.value),
  note,
  emptyText,
}) => {
  const [dimension, setDimension] = useState<Dimension>(dimensions[0]);

  const buckets = demographics?.[dimension];
  const rows = useMemo(
    () => (buckets ? toRows(dimension, buckets) : []),
    [dimension, buckets],
  );
  const totalRegistered = buckets ? sum(buckets, 'registered') : 0;
  const totalActive = buckets ? sum(buckets, 'active') : 0;

  return (
    <Card
      size='small'
      title={
        <span>
          ユーザー属性
          <Text type='secondary' className='text-[12px] font-normal ml-sm'>
            {moment(asOf)
              .utcOffset(9 * 60)
              .format('YYYY/MM/DD HH:mm')}{' '}
            時点
          </Text>
        </span>
      }
      extra={
        <Segmented
          size='small'
          options={DIMENSION_OPTIONS.filter((o) =>
            dimensions.includes(o.value),
          )}
          value={dimension}
          onChange={(v) => setDimension(v as Dimension)}
        />
      }
    >
      {!demographics ? (
        <Empty description={emptyText} />
      ) : (
        <div className='flex flex-col gap-sm'>
          <Text type='secondary' className='text-[12px]'>
            {note({ registered: totalRegistered, active: totalActive })}
          </Text>
          <Table<Row>
            size='small'
            rowKey='key'
            pagination={false}
            scroll={{ x: 560 }}
            dataSource={rows}
            columns={[
              { title: '', dataIndex: 'label', width: 160 },
              {
                title: (
                  <span>
                    <span
                      className='inline-block w-[8px] h-[8px] rounded-full mr-xs'
                      style={{ background: SERIES_COLORS[0] }}
                    />
                    登録
                  </span>
                ),
                key: 'registered',
                render: (_, r) => (
                  <div className='flex flex-col gap-[2px]'>
                    <div className='flex justify-between gap-sm'>
                      <span>{formatNumber(r.registered, '人')}</span>
                      <span className='text-fg-muted'>
                        {formatNumber(pct(r.registered, totalRegistered), '%')}
                      </span>
                    </div>
                    <ShareBar
                      value={pct(r.registered, totalRegistered)}
                      color={SERIES_COLORS[0]}
                    />
                  </div>
                ),
              },
              {
                title: (
                  <span>
                    <span
                      className='inline-block w-[8px] h-[8px] rounded-full mr-xs'
                      style={{ background: SERIES_COLORS[1] }}
                    />
                    アクティブ
                  </span>
                ),
                key: 'active',
                render: (_, r) => (
                  <div className='flex flex-col gap-[2px]'>
                    <div className='flex justify-between gap-sm'>
                      <span>{formatNumber(r.active, '人')}</span>
                      <span className='text-fg-muted'>
                        {formatNumber(pct(r.active, totalActive), '%')}
                      </span>
                    </div>
                    <ShareBar
                      value={pct(r.active, totalActive)}
                      color={SERIES_COLORS[1]}
                    />
                  </div>
                ),
              },
              {
                title: 'アクティブ率',
                key: 'activeRate',
                align: 'right',
                width: 110,
                render: (_, r) =>
                  formatNumber(pct(r.active, r.registered), '%'),
              },
            ]}
          />
        </div>
      )}
    </Card>
  );
};

/**
 * 登録ユーザーとアクティブユーザー（直近30日オンライン＝MAU と同じ基準）の属性の構成比。
 * 値は集計を実行した時点（executedAt）のもの。
 */
export const DemographicsCard: React.FC<{ latest: IKpiSnapshot }> = ({
  latest,
}) => (
  <DemographicsPanel
    demographics={latest.breakdowns.demographics}
    asOf={latest.executedAt}
    emptyText='まだ集計がありません（2026/10 の機能追加の翌朝から集計します）'
    note={({ registered: totalRegistered, active: totalActive }) => (
      <>
        登録＝退会していない全ユーザー（
        {formatNumber(totalRegistered, '人')}
        ）。アクティブ＝うち直近30日にアプリを開いた人（
        {formatNumber(totalActive, '人')}、MAU
        と同じ）。年齢は前日時点。住所は郵便番号の登録から
      </>
    )}
  />
);
